import type { IWorkspace } from "@/types/workspace";
import {
  DIGITAL_TEAMMATES_SETTINGS_FLAG,
  PROJECT_AUTOMATION_WORKSPACE_FLAG,
  SSO_SHARED_IDENTITY_PROVIDERS_FLAG,
  WEBHOOKS_FLAG,
} from "@/lib/launchdarkly/flags";

export type TWorkspacePermission =
  // Workspace. `workspace.create` / `workspace.delete` / `workspace.reset` and the
  // `workspace.template.use` / `manage` pair are Cognota-only (NG-1237): defined and
  // enforced server-side, surfaced in a workspace's `permissions` array for the
  // staff who hold them, but never assignable to a System Role — so they are read
  // through the capability helpers at the bottom of this file, not listed here.
  | "workspace.read"
  | "workspace.edit"
  | "workspace.member.manage"
  | "workspace.role.create"
  | "workspace.role.read"
  | "workspace.role.edit"
  | "workspace.role.delete"
  | "workspace.audit_log.manage"
  | "workspace.password_policy.read"
  | "workspace.password_policy.edit"
  | "workspace.session_policy.read"
  | "workspace.session_policy.edit"
  | "workspace.analytic.manage"
  | "workspace.aiProvider.manage"
  | "workspace.aiProvider.use"
  // Reading and changing the workspace's SSO settings (NG-3555): the SSO tab
  // and its "Stop using" action gate on these while sso-shared-identity-providers
  // is on, as the API always has. Not in ALL_PERMISSIONS: the roles grid doesn't
  // offer them (the backend catalogue lists them for the System Roles editor).
  | "workspace.sso.read"
  | "workspace.sso.edit"
  // FR20's admin recovery toggle (NG-1371) — suspends SSO enforcement for the
  // whole workspace, its own dedicated action distinct from workspace.sso.edit.
  | "workspace.sso.override"
  // Project
  | "project.create"
  | "project.read"
  // NG-1773 (the sheet's "Missing" singleton): the PROJECT-scoped counterpart
  // of request.project.read below — "see the requests linked to a project".
  | "project.request.read"
  | "project.edit"
  | "project.delete"
  | "project.comment"
  | "project.member.manage"
  | "project.smartAssign"
  // Work Item
  | "workitem.read"
  | "workitem.edit"
  // NG-1773: re-split out of workitem.edit (backend migration 0069 had folded
  // it in); the create endpoint now checks it directly.
  | "workitem.create"
  | "workitem.delete"
  | "workitem.comment"
  | "workitem.dependency.create"
  | "workitem.dependency.edit"
  | "workitem.dependency.read"
  | "workitem.dependency.delete"
  // Milestone (NG-1773, the Permission Mapping sheet's "Missing" column): the
  // milestone-kind counterparts of the workitem.* verbs above, gated per-kind
  // by the same generic comment/dependency/detail endpoints.
  // milestone.create joined at NG-2096 (sheet row 96): a real, assignable
  // System Role permission gating the milestone CREATE path (db/0458);
  // milestone.edit still gates every edit of an existing milestone under the
  // PRD #2795 rule.
  | "milestone.read"
  | "milestone.create"
  | "milestone.edit"
  | "milestone.delete"
  | "milestone.comment"
  | "milestone.dependency.create"
  | "milestone.dependency.edit"
  // Team
  | "team.create"
  | "team.read"
  | "team.edit"
  | "team.delete"
  | "team.report.manage"
  | "team.member.manage"
  // Request
  | "request.create"
  | "request.import"
  | "request.read"
  | "request.edit"
  | "request.delete"
  | "request.publicFields.read"
  | "request.publicFields.edit"
  | "request.privateFields.read"
  | "request.privateFields.edit"
  | "request.project.create"
  | "request.project.link"
  | "request.project.read"
  | "request.comment"
  | "request.owner.manage"
  | "request.reviewer.manage"
  | "request.client.manage"
  // Events
  | "event.create"
  | "event.read"
  | "event.edit"
  | "event.list"
  | "event.delete"
  // Calendars. `calendar.admin` (domain-wide Google/Microsoft sync) is
  // Cognota-only (NG-1932) — read through `hasCalendarAdminCapability` below,
  // not listed here, the same shape as the workspace.* Cognota-only set above.
  | "calendar.holiday.read"
  | "calendar.holiday.manage"
  // NG-1773: the event-kind counterparts of workitem.comment / dependency.*
  // above, gated per-kind by the same generic comment/dependency endpoints.
  | "event.comment"
  | "event.dependency.create"
  | "event.dependency.edit"
  // Capacity (kept as-is)
  | "capacity.create"
  | "capacity.read"
  | "capacity.edit"
  | "capacity.list"
  | "capacity.delete"
  // Dashboard
  | "dashboard.create"
  | "dashboard.read"
  | "dashboard.edit"
  | "dashboard.list"
  // dashboard.share (NG-1117): on by default for every role except Guest today;
  // the admin-configurable per-role toggle is NG-1257.
  | "dashboard.share"
  // The Reporting Teammate (NG-1773): every reporting-agent endpoint checks it.
  // No out-of-the-box role holds it (NG-3125), so `dashboard.create` alone no
  // longer implies it, and the Teammate's surfaces gate on this (NG-3216).
  | "reporting_agent"
  // Report (NG-2477): gates the Reports index's Create action; the only report.* key with a client surface.
  | "report.create"
  // Goals (NG-615): goal.read gates the Strategy nav — internal tiers and
  // collaborators hold it, guests don't. The mutating goal verbs stay probed
  // per-surface via the permission service, not mirrored here.
  | "goal.read"
  // Processes (NG-679): `process.read` gates the Processes settings TAB. Every
  // member holds it — option A makes the process bank member-readable, because
  // the app paints stage names from it everywhere — so this is reachability, not
  // manage authority. The mutating process verbs are deliberately absent: a team
  // lead's `process.create` lives at their TEAM's scope and can never appear in
  // this flat workspace-scope list, and per-process authority arrives on the row
  // as `can_edit` instead.
  | "process.read"
  // Project Automation (NG-3680, NG-3529): `automation.read` gates the Automations
  // settings tab and `automation.edit` its writes. Seeded on every Admin and
  // Standard role server-side (`system_role_seed._AUTOMATIONS`); keep the two
  // strings in sync with `core/rbac/automation_access.py`.
  | "automation.read"
  | "automation.edit"
  // Financial data (compensation-sensitive: rates and cost) — admin-only (#2761, #3668)
  | "financialData.view"
  | "financialData.manage"
  // Managed category ("System Field") rows under Global Fields (NG-1692). The
  // read/create/edit endpoints moved to a membership-only check (NG-1682) —
  // every member's category pickers need them — so these three now gate only
  // the Global Fields settings surface (the sheet's "View system categories
  // under Global Fields"), never the picker reads. Kept out of
  // `ALL_PERMISSIONS`: the assignable grid for this catalogue area is served
  // from the backend's permission catalogue (migration 0398), not this list.
  | "category.read"
  | "category.create"
  | "category.edit"
  // The request-prioritization config read, which the Requests list primes
  // only for a holder. Kept out of `ALL_PERMISSIONS` for the same reason as
  // the category keys above.
  | "priorities.read";

/** Nav-level Area names (NG-1453), seeded server-side from the Permission
 *  Mapping sheet via `rbac_permissions.category` (migration 0398). Only the
 *  Areas the sidebar actually gates on are listed here. */
export type TWorkspaceArea =
  | "Insights"
  | "Capacity"
  | "Requests"
  | "Programs"
  | "Projects"
  | "Work Items"
  | "Goals"
  | "Workspace settings";

export type TWorkspaceSettingsTabKey =
  | "general"
  | "teams"
  | "members"
  | "roles"
  | "job-roles"
  | "exports"
  | "audit-log"
  | "automations"
  | "webhooks"
  | "ai-provider"
  | "digital-teammates"
  | "sso"
  | "password-policy"
  | "session-timeout"
  | "oauth-apps"
  | "calendars"
  | "time-off"
  | "sla"
  | "request-prioritization"
  | "skills"
  | "forms"
  | "templates"
  | "custom-fields"
  | "statuses"
  | "processes"
  | "integration-library";

export type TWorkspaceSettingsGroupKey =
  | "general"
  | "people"
  | "foundations"
  | "operations"
  | "intelligence"
  | "integrations";

export interface IWorkspaceSettingsGroup {
  key: TWorkspaceSettingsGroupKey;
  labelKey: string;
}

export const WORKSPACE_SETTINGS_GROUPS: IWorkspaceSettingsGroup[] = [
  { key: "general", labelKey: "workspace_settings.groups.general" },
  { key: "people", labelKey: "workspace_settings.groups.people" },
  { key: "foundations", labelKey: "workspace_settings.groups.foundations" },
  { key: "operations", labelKey: "workspace_settings.groups.operations" },
  { key: "intelligence", labelKey: "workspace_settings.groups.intelligence" },
  { key: "integrations", labelKey: "workspace_settings.groups.integrations" },
];

export interface IWorkspaceSettingsTab {
  key: TWorkspaceSettingsTabKey;
  labelKey: string;
  path: string;
  group: TWorkspaceSettingsGroupKey;
  required: TWorkspacePermission[];
  /** LaunchDarkly keys, ANY of which turns this tab on. Absent means ungated.
   *
   *  A flag key is data, not a UI concern, which is why it sits here beside the
   *  permission list rather than in the nav component: the two answer the same
   *  question ("should this person see this tab") and splitting them is how one
   *  gets checked and the other forgotten.
   *
   *  A **list**, with any-of semantics, because a tab can host more than one
   *  independently-launched thing, and a single key would make each one hostage to
   *  the others' launch decisions. The Integration Library was the case that needed
   *  it, opening on any connector's key, and is ungated now that 360Learning shipped
   *  to everyone (NG-2072). */
  flags?: string[];
  /** A different permission list while one flag is on: `required` is the
   *  flag-off answer, and this one replaces it when `flag` is on. For a tab whose
   *  gate moves with a launch rather than a tab that appears with one. The SSO
   *  tab is the case: today `workspace.edit`, and `workspace.sso.read` under
   *  `sso-shared-identity-providers`, the action the API checks (NG-3555). */
  requiredWhenFlagOn?: { flag: string; required: TWorkspacePermission[] };
}

export const ALL_PERMISSIONS: TWorkspacePermission[] = [
  // Workspace (the Cognota-only actions are deliberately absent — see the type).
  "workspace.read",
  "workspace.edit",
  "workspace.member.manage",
  "workspace.role.create",
  "workspace.role.read",
  "workspace.role.edit",
  "workspace.role.delete",
  "workspace.audit_log.manage",
  "workspace.password_policy.read",
  "workspace.password_policy.edit",
  // Session-timeout settings (NG-1762) — admin-only, same shape as
  // workspace.password_policy.*.
  "workspace.session_policy.read",
  "workspace.session_policy.edit",
  "workspace.analytic.manage",
  // The FR20 SSO password-login override (NG-1371) — admin-only, its own
  // dedicated action rather than folded into workspace.sso.edit.
  "workspace.sso.override",
  // The workspace AI-provider setting (NG-1358) — admin-only, like the SSO and
  // connected-app grants: a credential-bearing integration surface.
  "workspace.aiProvider.manage",
  // Have the AI agents run on the workspace's provider (NG-1359) — base-tier,
  // held by every seeded member role; the reporting agent's credential fetch
  // is gated on it.
  "workspace.aiProvider.use",
  // Project
  "project.create",
  "project.read",
  "project.request.read",
  "project.edit",
  "project.delete",
  "project.comment",
  "project.member.manage",
  "project.smartAssign",
  // Work Item
  "workitem.read",
  "workitem.edit",
  "workitem.create",
  "workitem.delete",
  "workitem.comment",
  "workitem.dependency.create",
  "workitem.dependency.edit",
  "workitem.dependency.read",
  "workitem.dependency.delete",
  // Milestone (NG-1773; milestone.create added by NG-2096, db/0458)
  "milestone.read",
  "milestone.create",
  "milestone.edit",
  "milestone.delete",
  "milestone.comment",
  "milestone.dependency.create",
  "milestone.dependency.edit",
  // Team
  "team.create",
  "team.read",
  "team.edit",
  "team.delete",
  "team.report.manage",
  "team.member.manage",
  // Request
  "request.create",
  // Bulk CSV import (NG-1124) — held by every workspace tier except guest.
  "request.import",
  "request.read",
  "request.edit",
  "request.delete",
  "request.publicFields.read",
  "request.publicFields.edit",
  "request.privateFields.read",
  "request.privateFields.edit",
  "request.project.create",
  "request.project.link",
  "request.project.read",
  "request.comment",
  "request.owner.manage",
  "request.reviewer.manage",
  "request.client.manage",
  // Events
  "event.create",
  "event.read",
  "event.edit",
  "event.list",
  "event.delete",
  // Calendars. calendar.admin is Cognota-only (NG-1932) — deliberately absent,
  // same as the workspace.* Cognota-only set above.
  "calendar.holiday.read",
  "calendar.holiday.manage",
  "event.comment",
  "event.dependency.create",
  "event.dependency.edit",
  // Capacity
  "capacity.create",
  "capacity.read",
  "capacity.edit",
  "capacity.list",
  "capacity.delete",
  // Dashboard
  "dashboard.create",
  "dashboard.read",
  "dashboard.edit",
  "dashboard.list",
  "dashboard.share",
  "reporting_agent",
  // Goals (NG-615)
  "goal.read",
  // Processes (NG-679): grantable in a custom role, and the gate on the
  // Processes settings tab. Read only — the mutating process verbs are scoped
  // per-team/per-object and are not workspace-scope grants.
  "process.read",
  // Project Automation (NG-3680, NG-3529). The legacy grid has no `automation.`
  // category, so these never render there; the System Roles editor lists them
  // from the backend catalogue, behind PROJECT_AUTOMATION_WORKSPACE_FLAG.
  "automation.read",
  "automation.edit",
  // Financial data (compensation-sensitive: rates and cost) — admin-only (#2761, #3668)
  "financialData.view",
  "financialData.manage",
];

export const ADMIN_PERMISSIONS = ALL_PERMISSIONS;

// The Power User and Collaborator presets were retired with their roles (NG-1238):
// power users became admins and collaborators became standard users.
export const STANDARD_USER_PERMISSIONS: TWorkspacePermission[] = [];
export const GUEST_PERMISSIONS: TWorkspacePermission[] = [];

const WORKSPACE_SETTINGS_TABS: IWorkspaceSettingsTab[] = [
  {
    key: "general",
    labelKey: "workspace_settings.tabs.general",
    path: "",
    group: "general",
    required: ["workspace.read"],
  },
  {
    // First in People & Access, above Members (GH-3993 design). Gated on the
    // workspace-level `team.create` — admins and power users — which is what
    // makes the whole Teams management surface (create / edit / delete anywhere
    // in the tree) worth opening from Settings. A team lead without it still
    // manages their own team through the standalone `/teams` route.
    key: "teams",
    labelKey: "workspace_settings.tabs.teams",
    path: "teams",
    group: "people",
    required: ["team.create"],
  },
  {
    key: "members",
    labelKey: "workspace_settings.tabs.members",
    path: "members",
    group: "people",
    required: ["workspace.member.manage"],
  },
  {
    key: "roles",
    labelKey: "workspace_settings.tabs.roles",
    path: "roles",
    group: "people",
    required: ["workspace.role.read"],
  },
  {
    key: "job-roles",
    labelKey: "workspace_settings.tabs.job_roles",
    path: "job-roles",
    group: "people",
    required: ["workspace.edit"],
  },
  {
    key: "sso",
    labelKey: "workspace_settings.tabs.sso",
    path: "sso",
    group: "people",
    required: ["workspace.edit"],
    requiredWhenFlagOn: {
      flag: SSO_SHARED_IDENTITY_PROVIDERS_FLAG,
      required: ["workspace.sso.read"],
    },
  },
  {
    key: "password-policy",
    labelKey: "workspace_settings.tabs.password_policy",
    path: "password-policy",
    group: "people",
    // Granular workspace.password_policy.read (NG-1466), not workspace.edit —
    // this settings surface controls another admin's authentication
    // requirements, not general workspace config.
    required: ["workspace.password_policy.read"],
  },
  {
    key: "session-timeout",
    labelKey: "workspace_settings.tabs.session_timeout",
    path: "session-timeout",
    group: "people",
    // Granular workspace.session_policy.read (NG-1762), not workspace.edit —
    // same reasoning as password-policy immediately above.
    required: ["workspace.session_policy.read"],
  },
  {
    key: "oauth-apps",
    labelKey: "workspace_settings.tabs.oauth_apps",
    path: "oauth-apps",
    group: "integrations",
    required: ["workspace.edit"],
  },
  {
    key: "custom-fields",
    labelKey: "workspace_settings.tabs.custom_fields",
    path: "custom-fields",
    group: "foundations",
    required: ["workspace.edit"],
  },
  {
    key: "skills",
    labelKey: "workspace_settings.tabs.skills",
    // The URL segment is `qualifications` (NG-1650); the tab KEY stays `skills`.
    // Keep in sync with `getWorkspaceSettingsTabFromPath` below.
    path: "qualifications",
    group: "foundations",
    required: ["workspace.edit"],
  },
  {
    key: "statuses",
    labelKey: "workspace_settings.tabs.statuses",
    path: "statuses",
    group: "foundations",
    required: ["workspace.edit"],
  },
  {
    key: "processes",
    labelKey: "workspace_settings.tabs.processes",
    path: "processes",
    group: "foundations",
    // NG-679: reachability is `process.read`, NOT manage authority. `required` is
    // matched against the flat WORKSPACE-scope permission list, and a team lead's
    // `process.create` lives at their TEAM's scope — so there is no static
    // permission meaning "leads but not plain members", and gating on
    // `workspace.edit` hid the whole surface from the very people the feature is
    // for. This deliberately makes the tab visible to every member, read-only:
    // option A already makes the process bank member-readable everywhere and the
    // list is not reach-filtered, so authority is per-ROW (`can_edit`), not
    // per-tab.
    required: ["process.read"],
  },
  {
    key: "forms",
    labelKey: "workspace_settings.tabs.forms",
    path: "forms",
    group: "foundations",
    // Form authoring / management is admin-only (#3556): the legacy standalone
    // builder is retired and authoring goes through the admin-only workflow
    // builder, so the Intake Workflows settings surface is gated to admins.
    // ``workspace.member.manage`` is the admin sentinel (WORKSPACE_ADMIN_SENTINEL);
    // it's admin-only (excluded from power users), which ``workspace.edit`` is not.
    required: ["workspace.member.manage"],
  },
  {
    key: "templates",
    labelKey: "workspace_settings.tabs.templates",
    path: "templates",
    group: "foundations",
    required: ["workspace.edit"],
  },
  {
    key: "calendars",
    labelKey: "workspace_settings.tabs.calendars",
    path: "calendars",
    group: "operations",
    // NG-1932: the page's own reads gate on calendar.holiday.read (Holiday
    // Calendars management) — event.edit let a caller reach a tab whose every
    // call then 403'd. The domain-wide sync cards on this same page are a
    // separate, Cognota-only surface gated at the component level; see
    // hasCalendarAdminCapability below.
    required: ["calendar.holiday.read"],
  },
  {
    key: "time-off",
    labelKey: "workspace_settings.tabs.time_off",
    path: "time-off",
    group: "operations",
    required: ["capacity.edit"],
  },
  {
    key: "request-prioritization",
    labelKey: "workspace_settings.tabs.request_prioritization",
    path: "request-prioritization",
    group: "intelligence",
    required: ["workspace.edit"],
  },
  {
    key: "sla",
    labelKey: "workspace_settings.tabs.sla",
    path: "slas",
    group: "intelligence",
    required: ["workspace.edit"],
  },
  {
    // NG-2622 (WS2-F): which digital teammates the workspace may use. Gated on
    // `workspace.edit`, the action the endpoints enforce and the same one as the
    // MCP access ceiling, which is the same kind of decision (design F2). Dark
    // behind its flag until product turns it on.
    key: "digital-teammates",
    labelKey: "workspace_settings.tabs.digital_teammates",
    path: "digital-teammates",
    group: "intelligence",
    required: ["workspace.edit"],
    flags: [DIGITAL_TEAMMATES_SETTINGS_FLAG],
  },
  {
    key: "exports",
    labelKey: "workspace_settings.tabs.exports",
    path: "exports",
    group: "operations",
    required: ["workspace.edit"],
  },
  {
    key: "audit-log",
    labelKey: "workspace_settings.tabs.audit_log",
    path: "audit-log",
    group: "operations",
    required: ["workspace.audit_log.manage"],
  },
  {
    // NG-3529: the workspace's automation defaults, copied to each new team. Gated
    // on `automation.read`, the action every endpoint the page reads enforces
    // (`core/rbac/automation_access.py`); `automation.edit` gates the writes at the
    // control. Dark behind Project Automation's first-release flag until product
    // turns it on.
    key: "automations",
    labelKey: "workspace_settings.tabs.automations",
    path: "automations",
    group: "operations",
    required: ["automation.read"],
    flags: [PROJECT_AUTOMATION_WORKSPACE_FLAG],
  },
  {
    // Hidden behind its flag, off in production: only work-item events are ever
    // delivered, so most of the editor's subscriptions never fire (NG-2330,
    // NG-591). The tab key covers the detail page too, via
    // getWorkspaceSettingsTabFromPath.
    key: "webhooks",
    labelKey: "workspace_settings.tabs.webhooks",
    path: "webhooks",
    group: "integrations",
    required: ["workspace.edit"],
    flags: [WEBHOOKS_FLAG],
  },
  {
    // Gated on the manage action rather than `workspace.edit` like its siblings:
    // every endpoint the page reads is `workspace.aiProvider.manage`-guarded
    // (including the GET), so a power user with only `workspace.edit` would land
    // on a page that cannot load. Hidden is the honest treatment; a deep link
    // gets the layout's access-denied screen. NG-1362.
    key: "ai-provider",
    labelKey: "workspace_settings.tabs.ai_provider",
    path: "ai-provider",
    group: "integrations",
    required: ["workspace.aiProvider.manage"],
  },
  {
    key: "integration-library",
    labelKey: "workspace_settings.tabs.integration_library",
    path: "integration-library",
    group: "integrations",
    // `workspace.read`, not `workspace.edit` (NG-1619): the catalog answers "do
    // you integrate with X", which is a question most of the workspace has and,
    // while this required `workspace.edit`, almost nobody could answer. Every
    // write on the page — connect, pause, disconnect, download the Teams package
    // — still checks `workspace.edit` at the control and at the API.
    required: ["workspace.read"],
    // No `flags`: 360Learning is available to every workspace (NG-2072), so the
    // library always has a connector to show. It opened on any connector's list
    // flag until that flag was retired, and the route applied the same test.
  },
];

/**
 * Cognota-only capabilities (NG-1237). Carried by the instance-scope
 * `instance.sales` grant (and the instance-admin wildcard) and intentionally kept
 * out of `ALL_PERMISSIONS` / the assignable role-permission grid — no System Role
 * can hold them, so they must not appear in the role editor.
 *
 * `workspace.create` is read off the user's instance standing
 * (`user.hasInstanceCapability`), never off a workspace payload: a rep with no
 * workspace yet has nothing to scan. The per-workspace ones (`delete`, `reset`, the
 * template verbs) ARE read off the workspace GET payload, because the server
 * decides per workspace whether the caller may act on it (demo-only, per-person).
 */
export const WORKSPACE_CREATE_CAPABILITY = "workspace.create";
const WORKSPACE_DELETE_CAPABILITY = "workspace.delete";
const WORKSPACE_RESET_CAPABILITY = "workspace.reset";

/** `workspace.delete` is emitted only on demo workspaces (the server strips it on
 *  real ones), so this needs no client-side demo check. The strip holds for an
 *  instance admin too, who may still delete a real workspace, so a caller that must
 *  honour that checks `user.isInstanceAdmin` beside this. */
export function hasWorkspaceDeleteCapability(workspace: IWorkspace | null | undefined): boolean {
  return workspace?.permissions?.includes(WORKSPACE_DELETE_CAPABILITY) ?? false;
}

export function hasWorkspaceResetCapability(workspace: IWorkspace | null | undefined): boolean {
  return workspace?.permissions?.includes(WORKSPACE_RESET_CAPABILITY) ?? false;
}

const WORKITEM_LIST_PERMISSION = "workitem.list";

/** `workitem.list`, which the workspace Tasks list GET (`WorkspaceIssueListEndpoint`)
 *  and the worklog summary require at the workspace scope; the Guest role does not
 *  hold it (NG-1775). Read off the raw list: it isn't in `TWorkspacePermission`,
 *  whose keys also drive the role editor's grid, so `hasWorkspacePermission` and
 *  `PermissionGuard` can't name it. */
export function hasWorkItemListPermission(workspace: IWorkspace | null | undefined): boolean {
  return workspace?.permissions?.includes(WORKITEM_LIST_PERMISSION) ?? false;
}

const SKILL_ASSIGN_PERMISSION = "skill.assign";

/** `skill.assign` at the workspace scope, which lets the member qualifications PUT
 *  through for any member. A Team Lead without it is asked per target instead
 *  (`team.member.skills.manage`, NG-3285). Adding a qualification to a work item
 *  asks it too, by hand and in bulk (NG-3466), beside edit rights on the item, so
 *  the project Work items bar's Add qualifications reads it (NG-3468). Read off
 *  the raw list, as {@link hasWorkItemListPermission} is. */
export function hasSkillAssignPermission(workspace: IWorkspace | null | undefined): boolean {
  return workspace?.permissions?.includes(SKILL_ASSIGN_PERMISSION) ?? false;
}

/**
 * `calendar.admin` (NG-1932) — domain-wide Google Workspace / Microsoft 365 sync,
 * a service-account delegation product does not offer customers. Like
 * {@link WORKSPACE_RESET_CAPABILITY}, it's Cognota-only (instance-scope), read off
 * the workspace GET payload rather than listed in `TWorkspacePermission`.
 * An instance admin's array carries it since NG-2144 (the array is what the
 * backend's `can()` allows, their wildcard included); callers still check
 * `user.isInstanceAdmin` beside it, as for workspace deletion, so a payload from a
 * backend before that keeps the cards. Gates the
 * domain-wide sync cards on the Calendars settings page; the
 * Holiday Calendars card beside them is an ordinary permission
 * (`calendar.holiday.read`), listed in `TWorkspacePermission` above.
 */
const WORKSPACE_CALENDAR_ADMIN_CAPABILITY = "calendar.admin";

export function hasCalendarAdminCapability(workspace: IWorkspace | null | undefined): boolean {
  return workspace?.permissions?.includes(WORKSPACE_CALENDAR_ADMIN_CAPABILITY) ?? false;
}

/**
 * Workspace-template capabilities. Like {@link WORKSPACE_RESET_CAPABILITY}, these
 * are Cognota-only (instance-scope) and surfaced in the workspace GET
 * `permissions` array, but are intentionally kept out of `ALL_PERMISSIONS` /
 * the assignable role grid. `use` gates *seeing* the template list (incl.
 * `restricted` templates) and resetting to one; `manage` additionally gates
 * creating and deleting templates.
 */
const WORKSPACE_TEMPLATES_USE_CAPABILITY = "workspace.template.use";
const WORKSPACE_TEMPLATES_MANAGE_CAPABILITY = "workspace.template.manage";

export function hasWorkspaceTemplatesUseCapability(
  workspace: IWorkspace | null | undefined,
): boolean {
  return workspace?.permissions?.includes(WORKSPACE_TEMPLATES_USE_CAPABILITY) ?? false;
}

export function hasWorkspaceTemplatesManageCapability(
  workspace: IWorkspace | null | undefined,
): boolean {
  return workspace?.permissions?.includes(WORKSPACE_TEMPLATES_MANAGE_CAPABILITY) ?? false;
}

/**
 * Whether the Reporting Teammate may be offered to the current user (NG-256): the role
 * holds `reporting_agent` AND the workspace's AI is usable. A role grant alone isn't
 * enough, because the server refuses the agent with 403 `ai_feature_disabled` while the
 * workspace's master AI switch or the teammate's own switch is off, and showing an entry
 * point that can only fail is a dead end. A payload from before the field reads as on.
 */
export function canUseReportingAgent(workspace: IWorkspace | null | undefined): boolean {
  return (
    hasWorkspacePermission(workspace, "reporting_agent") &&
    workspace?.reporting_agent_enabled !== false
  );
}

export function hasWorkspacePermission(
  workspace: IWorkspace | null | undefined,
  permission: TWorkspacePermission,
): boolean {
  return workspace?.permissions?.includes(permission) ?? false;
}

/**
 * Whether the Capacity page opens for the current user (NG-2113). Not
 * `hasWorkspacePermission(ws, "capacity.read")`: a team lead holds `capacity.*`
 * at their team, which the workspace `permissions` array never carries, so that
 * check refused the person the capacity endpoints already serve. The server
 * decides it as `can_view_capacity`; a payload from before the field falls back
 * to the workspace-scope check, today's answer.
 */
export function canOpenCapacity(workspace: IWorkspace | null | undefined): boolean {
  return workspace?.can_view_capacity ?? hasWorkspacePermission(workspace, "capacity.read");
}

/**
 * Permission that distinguishes a workspace admin from a power user. Member
 * management is in {@link POWER_USER_EXCLUDED}, so only the admin role carries
 * it — making it the canonical "is this user an admin" signal (the same gate
 * the workspace MCP access card uses). The Form Workflow Builder is admin-only
 * (PRD §5.1), so its tab gates on this.
 */
const WORKSPACE_ADMIN_SENTINEL: TWorkspacePermission = "workspace.member.manage";

export function isWorkspaceAdmin(workspace: IWorkspace | null | undefined): boolean {
  return hasWorkspacePermission(workspace, WORKSPACE_ADMIN_SENTINEL);
}

/** Whether the member holds at least one permission in `area` (NG-1453).
 *  An absent `visible_areas` (an older cached payload) means "all Areas
 *  visible" — never treat the absence of the field as a reason to hide. */
export function hasWorkspaceVisibleArea(
  workspace: IWorkspace | null | undefined,
  area: TWorkspaceArea,
): boolean {
  const areas = workspace?.visible_areas;
  if (areas === undefined) return true;
  return areas.includes(area);
}

export function hasAnyWorkspacePermission(
  workspace: IWorkspace | null | undefined,
  permissions: TWorkspacePermission[],
): boolean {
  if (!workspace?.permissions?.length) return false;
  return permissions.some((p) => workspace.permissions!.includes(p));
}

export function getWorkspaceSettingsTab(key: TWorkspaceSettingsTabKey): IWorkspaceSettingsTab {
  const tab = WORKSPACE_SETTINGS_TABS.find((candidate) => candidate.key === key);
  if (!tab) throw new Error(`Unknown workspace settings tab: ${key}`);
  return tab;
}

/** The tabs this person can see.
 *
 *  ``isFlagEnabled`` **fails closed**: with no predicate supplied, a tab carrying a
 *  flag is hidden. A caller that forgets to pass one therefore under-shows rather
 *  than shipping a dark surface to everyone, which is the failure worth having.
 */
/** The LaunchDarkly keys gating this tab, or undefined when it is ungated.
 *
 *  Exists so a caller can tell "this tab is hidden because its flags are off" apart
 *  from "this tab is hidden because the flags have not loaded yet" — a distinction the
 *  settings layout has to make before it redirects, and could not make while the flag
 *  map was private to this module. The layout only needs to know *whether* the tab is
 *  gated, so which keys they are is informational there.
 */
export function workspaceSettingsTabFlags(key: TWorkspaceSettingsTabKey): string[] | undefined {
  const tab = WORKSPACE_SETTINGS_TABS.find((candidate) => candidate.key === key);
  if (!tab) return undefined;
  // A flag that moves the tab's permission gate decides access too, so the layout
  // waits for it as it waits for a flag that shows the tab.
  const keys = [
    ...(tab.flags ?? []),
    ...(tab.requiredWhenFlagOn ? [tab.requiredWhenFlagOn.flag] : []),
  ];
  return keys.length > 0 ? keys : undefined;
}

/** The permissions a tab asks for under the current flags: `requiredWhenFlagOn`'s
 *  list while its flag is on, else `required`. Fails closed like the visibility
 *  check: with no predicate, every flag reads off. */
export function workspaceSettingsTabRequired(
  tab: IWorkspaceSettingsTab,
  isFlagEnabled: (key: string) => boolean = () => false,
): TWorkspacePermission[] {
  const moved = tab.requiredWhenFlagOn;
  return moved && isFlagEnabled(moved.flag) ? moved.required : tab.required;
}

export function getVisibleWorkspaceSettingsTabs(
  workspace: IWorkspace | null | undefined,
  isFlagEnabled: (key: string) => boolean = () => false,
): IWorkspaceSettingsTab[] {
  return WORKSPACE_SETTINGS_TABS.filter(
    (tab) =>
      hasAnyWorkspacePermission(workspace, workspaceSettingsTabRequired(tab, isFlagEnabled)) &&
      (tab.flags === undefined || tab.flags.some(isFlagEnabled)),
  );
}

export function getWorkspaceSettingsTabFromPath(pathname: string): TWorkspaceSettingsTabKey {
  // Teams is checked FIRST: its detail/edit paths nest a second `settings`
  // segment (`/settings/teams/:id/settings/members`), which would otherwise be
  // swallowed by the `/settings/members` branch below and highlight Members.
  if (pathname.includes("/settings/teams")) return "teams";
  if (pathname.includes("/settings/members")) return "members";
  if (pathname.includes("/settings/job-roles")) return "job-roles";
  if (pathname.includes("/settings/roles")) return "roles";
  if (pathname.includes("/settings/audit-log")) return "audit-log";
  if (pathname.includes("/settings/automations")) return "automations";
  if (pathname.includes("/settings/exports")) return "exports";
  if (pathname.includes("/settings/webhooks")) return "webhooks";
  if (pathname.includes("/settings/ai-provider")) return "ai-provider";
  if (pathname.includes("/settings/integration-library")) return "integration-library";
  if (pathname.includes("/settings/sso")) return "sso";
  if (pathname.includes("/settings/password-policy")) return "password-policy";
  if (pathname.includes("/settings/session-timeout")) return "session-timeout";
  if (pathname.includes("/settings/digital-teammates")) return "digital-teammates";
  if (pathname.includes("/settings/oauth-apps")) return "oauth-apps";
  if (pathname.includes("/settings/calendars")) return "calendars";
  if (pathname.includes("/settings/time-off")) return "time-off";
  if (pathname.includes("/settings/request-prioritization")) return "request-prioritization";
  if (pathname.includes("/settings/slas")) return "sla";
  // Legacy Standard Fields path redirects into the consolidated Global Fields
  // (custom-fields) surface; keep the nav highlight on it during the redirect.
  if (pathname.includes("/settings/standard-fields")) return "custom-fields";
  if (pathname.includes("/settings/custom-fields")) return "custom-fields";
  if (pathname.includes("/settings/templates")) return "templates";
  if (pathname.includes("/settings/forms")) return "forms";
  if (pathname.includes("/settings/qualifications")) return "skills";
  if (pathname.includes("/settings/processes")) return "processes";
  // Consolidated Statuses page + legacy paths that redirect into it.
  if (pathname.includes("/settings/decline-reasons")) return "statuses";
  if (pathname.includes("/settings/statuses")) return "statuses";
  if (pathname.includes("/settings/request-statuses")) return "statuses";
  if (pathname.includes("/settings/project-states")) return "statuses";
  if (pathname.includes("/settings/states")) return "statuses";
  return "general";
}
